import { useCallback, useEffect, useRef, useState } from 'react';
import { wsBase } from '../config/ws';

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected';

export interface NowPlayingSnapshot {
    trackId: number | null;
    positionSeconds: number;
    isPlaying: boolean;
}

interface UseNowPlayingSyncResult {
    connectionStatus: ConnectionStatus;
    reportState: (trackId: number, positionSeconds: number, isPlaying: boolean) => void;
}

const MAX_BACKOFF_MS = 30000;
const INITIAL_BACKOFF_MS = 1000;

/**
 * Keeps a WebSocket connection to /api/ws/nowplaying alive, reconnecting
 * with exponential backoff on drop. Delivers the server's one-time resume
 * snapshot via onSnapshot, and exposes reportState to push local playback
 * changes back to the server.
 */
export function useNowPlayingSync(
    enabled: boolean,
    onSnapshot: (snapshot: NowPlayingSnapshot) => void
): UseNowPlayingSyncResult {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const socketRef = useRef<WebSocket | null>(null);
    const gotSnapshotRef = useRef(false);
    const reconnectDelayRef = useRef(INITIAL_BACKOFF_MS);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closedByUsRef = useRef(false);
    const onSnapshotRef = useRef(onSnapshot);
    onSnapshotRef.current = onSnapshot;

    const connect = useCallback(() => {
        setConnectionStatus('connecting');
        gotSnapshotRef.current = false;

        const socket = new WebSocket(`${wsBase()}/api/ws/nowplaying`);
        socketRef.current = socket;

        socket.onopen = () => {
            setConnectionStatus('connected');
            reconnectDelayRef.current = INITIAL_BACKOFF_MS;
        };

        socket.onmessage = (event) => {
            if (gotSnapshotRef.current) return;
            gotSnapshotRef.current = true;
            try {
                const data = JSON.parse(event.data);
                onSnapshotRef.current({
                    trackId: data.track_id ?? null,
                    positionSeconds: data.position_seconds ?? 0,
                    isPlaying: Boolean(data.is_playing),
                });
            } catch {
                // Ignore a malformed snapshot - nothing to resume from.
            }
        };

        socket.onclose = () => {
            setConnectionStatus('disconnected');
            socketRef.current = null;
            if (closedByUsRef.current) return;
            reconnectTimerRef.current = setTimeout(connect, reconnectDelayRef.current);
            reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, MAX_BACKOFF_MS);
        };

        socket.onerror = () => {
            socket.close();
        };
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        closedByUsRef.current = false;
        connect();

        return () => {
            closedByUsRef.current = true;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
            }
            socketRef.current?.close();
            socketRef.current = null;
            setConnectionStatus('disconnected');
        };
    }, [enabled, connect]);

    const reportState = useCallback((trackId: number, positionSeconds: number, isPlaying: boolean) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ track_id: trackId, position_seconds: positionSeconds, is_playing: isPlaying }));
    }, []);

    return { connectionStatus, reportState };
}
